import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { Locale } from '@bolao/shared';
import { authClient, useSession } from '../auth-client.js';
import AvatarUploader from '../components/AvatarUploader.js';
import LanguageToggle from '../components/LanguageToggle.js';
import ThemeToggle from '../components/ThemeToggle.js';
import styles from './SettingsScreen.module.css';

/**
 * Owns the language choice, replacing the interim `LanguageToggle` that used
 * to sit in the corner of every signed-in screen (PLAN.md Phase 7).
 *
 * Selecting a language does two things: `LanguageToggle` itself switches the
 * UI immediately and writes to `localStorage` (unchanged from before), and
 * this screen additionally persists the choice to `users.locale` via Better
 * Auth's `updateUser`, so it follows the account across devices rather than
 * living only in this browser.
 */
export default function SettingsScreen() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  const persistLocale = (locale: Locale) => {
    setSaved(false);
    setError(false);
    // Better Auth's client type doesn't know about our `locale` additional
    // field without wiring its type-inference plugin across the workspace
    // boundary (see the same tradeoff in App.tsx) — a cast here is simpler
    // than that for one nullable string it does accept at runtime.
    void authClient.updateUser({ locale } as Record<string, unknown>).then(
      () => setSaved(true),
      () => setError(true),
    );
  };

  return (
    <main className="screen">
      <Link to="/" className="muted">
        {t('common.back')}
      </Link>

      <h1>{t('settings.title')}</h1>

      <section className={`panel ${styles.section}`}>
        <h2 className={styles.sectionTitle}>{t('settings.languageTitle')}</h2>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          {t('settings.languageHint')}
        </p>
        <LanguageToggle onChange={persistLocale} />
        {saved ? <p className={styles.saved}>{t('settings.languageSaved')}</p> : null}
        {error ? <p className="error">{t('settings.languageSaveFailed')}</p> : null}
      </section>

      <section className={`panel ${styles.section}`}>
        <h2 className={styles.sectionTitle}>{t('settings.themeTitle')}</h2>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          {t('settings.themeHint')}
        </p>
        <ThemeToggle />
      </section>

      {session?.user ? (
        <section className={`panel ${styles.section}`}>
          <h2 className={styles.sectionTitle}>{t('settings.accountTitle')}</h2>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {t('auth.signedInAs', { name: session.user.name || session.user.email })}
          </p>
          <AvatarUploader
            name={session.user.name || session.user.email}
            initialImageUrl={(session.user as { image?: string | null }).image ?? null}
          />
        </section>
      ) : null}

      <section className={`panel ${styles.section}`}>
        <h2 className={styles.sectionTitle}>{t('settings.legalTitle')}</h2>
        <div className={styles.legalLinks}>
          <Link to="/privacy">{t('settings.privacyLink')}</Link>
          <Link to="/terms">{t('settings.termsLink')}</Link>
        </div>
        {/* Required attribution per football-data.org's terms of use — exact
            wording, not translated, since it names the source by its own
            phrasing rather than describing it in our own words. */}
        <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-2)' }}>
          {t('settings.footballDataAttribution')}
        </p>
      </section>
    </main>
  );
}
